import { z } from "zod";

import { appRoleSchema } from "./roles.ts";

/**
 * Mitarbeiter-Stammdaten (Stufe 2) — geteilte Shapes für Anlage und
 * Profil-Pflege. Profile speisen die Kapazitäts-/KPI-Berechnung
 * (Faktura-Soll = Arbeitstage(Bundesland) × h/Tag − Abwesenheiten).
 */

/** Bundesland-Codes wie in feiertagejs (Feiertagskalender). */
export const FEDERAL_STATES = [
  "BW", "BY", "BE", "BB", "HB", "HH", "HE", "MV",
  "NI", "NW", "RP", "SL", "SN", "ST", "SH", "TH",
] as const;
export type FederalState = (typeof FEDERAL_STATES)[number];
export const federalStateSchema = z.enum(FEDERAL_STATES);

/**
 * Body für POST /users/employees — legt Mitarbeiter OHNE Login an (Berater/
 * Externe für Kapazität & Ausreißeranalyse). Entra-Login verknüpft später
 * per E-Mail (findOrCreateEmployee sucht zuerst nach email).
 */
export const createEmployeeSchema = z.object({
  email: z.string().email().max(320),
  fullName: z.string().min(1).max(200),
});
export type CreateEmployeeInput = z.infer<typeof createEmployeeSchema>;

/** Body für PUT /users/employees/:id/profile (Upsert). */
export const employeeProfileSchema = z.object({
  careerLevel: z.string().min(1).max(100).nullable(),
  federalState: federalStateSchema,
  hoursPerDay: z.number().min(0).max(12),
  /** Home-Bereich — bestimmt, in welches Segment-Faktura-Soll der MA zählt. */
  segmentId: z.string().uuid().nullable(),
  mentorEmployeeId: z.string().uuid().nullable(),
  yppDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
});
export type EmployeeProfileInput = z.infer<typeof employeeProfileSchema>;

/** Body für POST /users/employees/:id/hours-periods. */
export const hoursPeriodFieldsSchema = z.object({
  validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  validTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  hoursPerDay: z.number().min(0).max(12),
});
export const hoursPeriodSchema = hoursPeriodFieldsSchema.refine((p) => p.validTo === null || p.validTo >= p.validFrom, {
    message: "validTo must not be before validFrom",
  });
export type HoursPeriodInput = z.infer<typeof hoursPeriodSchema>;

/** Query für GET /users (die Route wandelt `active` in einen Boolean um). */
export const userListQuerySchema = z.object({
  active: z.enum(["true", "false"]).transform((v) => v === "true").optional(),
  role: appRoleSchema.optional(),
  segmentId: z.string().uuid().optional(),
});

/** Body für POST /users/:employeeId/roles. */
export const grantRoleSchema = z.object({
  role: appRoleSchema,
  segmentId: z.string().uuid().optional(),
});

/** Felder von PATCH /users/:employeeId; die Route verlangt mindestens eines davon. */
export const userUpdateFieldsSchema = z.object({
  active: z.boolean().optional(),
  fullName: z.string().min(1).max(200).optional(),
});
export const userUpdateSchema = userUpdateFieldsSchema.refine(
  (b) => b.active !== undefined || b.fullName !== undefined,
  { message: "Provide active or fullName." },
);

/** Body für PATCH /users/me. */
export const ownNameSchema = z.object({
  fullName: z.string().min(1).max(200),
});
